import { fetchApiGraphQL, type GraphQLError } from "@/utils/fetcher";
import { createEnrichmentIsland } from "@/utils/islands";
import { whenLoggedIn } from "../Login/LoginStateElement";
import { showFormMessage } from "../formMessages";
import { emailFormatRule, setupFieldValidation } from "../formValidation";
import { resolveCallbackUrl } from "../callbackUrl";
import { openAffiliation } from "../Affiliation/affiliation";
import { affiliationLabel, getAffiliations, removeAffiliation } from "../Affiliation/affiliationStore";
import { meQuery, type MeQueryResult } from "../Store/userstore";
import { setUserAffiliations } from "../Store/useraffiliationstore";
import { getPostalCodes, type PostalCode } from "../postalCodes";



type UserProfile = {
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string;
  address: string | null;
  phoneNumber: string | null;
  postalCode: string | null;
  emailVerified: boolean;
};

type UpdateUserProfileResult = {
  data: {
    updateUserProfile: UserProfile | null;
  };
};

type EditableField = keyof Omit<UserProfile, 'name' | 'emailVerified'>;

// Postal code autocomplete state
let postalCodes: PostalCode[] = [];

const updateProfileMutation = `
  mutation UpdateUserProfile($email: String, $address: String, $phoneNumber: String, $firstName: String, $lastName: String, $postalCode: String) {
    updateUserProfile(email: $email, address: $address, phoneNumber: $phoneNumber, firstName: $firstName, lastName: $lastName, postalCode: $postalCode) {
      name
      firstName
      lastName
      email
      address
      phoneNumber
      postalCode
      emailVerified
    }
  }
`;

type SendVerificationEmailResult = {
  data: {
    sendVerificationEmail: boolean | null;
  };
};

const sendVerificationEmailMutation = `
  mutation SendVerificationEmail($callBackUrl: String!) {
    sendVerificationEmail(callBackUrl: $callBackUrl)
  }
`;

const fieldInputIds: Record<EditableField, string> = {
  firstName: 'FirstName',
  lastName: 'LastName',
  email: 'Email',
  address: 'Address',
  phoneNumber: 'PhoneNumber',
  postalCode: 'PostalCode',
};

function fillForm(form: HTMLFormElement, profile: UserProfile) {
  for (const [field, inputId] of Object.entries(fieldInputIds)) {
    const input = form.querySelector<HTMLInputElement>(`#${inputId}`);
    if (input) {
      input.value = profile[field as keyof typeof fieldInputIds] ?? '';
    }
  }

  fillCity(form, profile.postalCode ?? '');

  form.querySelectorAll<HTMLElement>('[data-email-status]').forEach(el => {
    const showWhenVerified = el.dataset.emailStatus === 'verified';
    el.classList.toggle('hidden', showWhenVerified !== profile.emailVerified);
  });

  form.querySelector<HTMLElement>('[data-verify-email]')
    ?.classList.toggle('hidden', profile.emailVerified);

  syncVerifyEmailButton(form);
}

let loadedProfile: UserProfile | null = null;

const loadPostalCodes = (): Promise<PostalCode[]> => getPostalCodes().catch(() => []);

function fillCity(form: HTMLFormElement, code: string): boolean {
  const city = postalCodes.find(pc => pc.code === code)?.city ?? '';
  const input = form.querySelector<HTMLInputElement>('#City');
  if (input) {
    input.value = city;
  }
  return city !== '';
}

function filterPostalCodeSuggestions(input: string): PostalCode[] {
  const searchTerm = input.toLowerCase();
  return postalCodes.filter(pc => 
    pc.code.toLowerCase().startsWith(searchTerm) || 
    pc.city.toLowerCase().startsWith(searchTerm)
  );
}

function renderPostalCodeSuggestions(form: HTMLFormElement, suggestions: PostalCode[]) {
  const container = form.querySelector<HTMLElement>('[data-postal-code-suggestions]');
  if (!container) return;

  container.innerHTML = '';
  
  if (suggestions.length === 0) {
    container.classList.add('hidden');
    return;
  }

  container.classList.remove('hidden');
  
  for (const pc of suggestions.slice(0, 10)) {
    const div = document.createElement('div');
    div.className = 'postal-code-suggestion cursor-pointer hover:bg-gray-100 p-2';
    div.textContent = `${pc.code} - ${pc.city}`;
    div.dataset.code = pc.code;
    div.dataset.city = pc.city;
    
    div.addEventListener('click', () => {
      const input = form.querySelector<HTMLInputElement>('#PostalCode');
      if (input) {
        input.value = pc.code;
        fillCity(form, pc.code);
        input.focus();
        container.classList.add('hidden');
      }
    });
    
    container.appendChild(div);
  }
}

function setupPostalCodeAutocomplete(form: HTMLFormElement) {
  const input = form.querySelector<HTMLInputElement>('#PostalCode');
  const suggestionsContainer = form.querySelector<HTMLElement>('[data-postal-code-suggestions]');
  
  if (!input || !suggestionsContainer) return;

  input.addEventListener('input', async () => {
    const value = input.value.trim();
    const matched = fillCity(form, value);
    if (value.length < 2 || (value.length === 4 && matched)) {
      suggestionsContainer.classList.add('hidden');
      return;
    }

    const suggestions = filterPostalCodeSuggestions(value);
    renderPostalCodeSuggestions(form, suggestions);
  });

  input.addEventListener('focus', () => {
    const value = input.value.trim();
    if (value.length >= 2) {
      const suggestions = filterPostalCodeSuggestions(value);
      renderPostalCodeSuggestions(form, suggestions);
    }
  });

  input.addEventListener('blur', () => {
    setTimeout(() => {
      suggestionsContainer.classList.add('hidden');
    }, 200);
  });
}

function setupProfileValidation(form: HTMLFormElement) {
  const email = form.querySelector<HTMLInputElement>('#Email');
  if (email) {
    setupFieldValidation(email, [emailFormatRule]);
  }
}

async function setupAffiliations(form: HTMLFormElement) {
  const list = form.querySelector<HTMLElement>('[data-affiliation-list]');
  const empty = form.querySelector<HTMLElement>('[data-affiliation-empty]');
  const rowTemplate = form.querySelector<HTMLTemplateElement>('template[data-affiliation-row]');
  const addButton = form.querySelector<HTMLButtonElement>('[data-add-affiliation]');

  if (!list || !rowTemplate || !addButton) {
    return;
  }

  const render = async () => {
    const affiliations = await getAffiliations();
    setUserAffiliations(affiliations.map(a => a.affiliationType));
    list.replaceChildren();

    for (const affiliation of affiliations) {
      const row = rowTemplate.content.cloneNode(true) as DocumentFragment;
      const item = row.querySelector<HTMLElement>('[data-affiliation-item]');
      const label = row.querySelector<HTMLElement>('[data-affiliation-label]');

      if (item) {
        item.dataset.affiliationType = affiliation.affiliationType;
        if (affiliation.affiliationId) {
          item.dataset.affiliationId = affiliation.affiliationId;
        }
      }
      if (label) {
        label.textContent = affiliationLabel(affiliation.affiliationType);
      }

      list.appendChild(row);
    }

    empty?.classList.toggle('hidden', affiliations.length > 0);
  };

  addButton.addEventListener('click', () => openAffiliation({ onSaved: render }));

  list.addEventListener('click', async (e) => {
    const target = e.target as HTMLElement;
    const item = target.closest<HTMLElement>('[data-affiliation-item]');
    const affiliationType = item?.dataset.affiliationType;
    const affiliationId = item?.dataset.affiliationId;
    if (!item || !affiliationType) {
      return;
    }

    if (target.closest('[data-edit-affiliation]')) {
      if (!affiliationId) {
        showFormMessage(form, 'failed');
        return;
      }

      const affiliations = await getAffiliations();
      const affiliation = affiliations.find(a => a.affiliationId === affiliationId);
      if (affiliation) {
        openAffiliation({ affiliation, onSaved: render });
      }
      return;
    }

    if (target.closest('[data-delete-affiliation]')) {
      const label = item.querySelector<HTMLElement>('[data-affiliation-label]')?.textContent ?? '';
      if (!window.confirm(`Vil du slette din tilknytning "${label}"?`)) {
        return;
      }

      if (!affiliationId) {
        showFormMessage(form, 'failed');
        return;
      }

      if (await removeAffiliation(affiliationId)) {
        showFormMessage(form, 'failed');
        return;
      }
      await render();
    }
  });

  await render();
}

async function loadProfile(form: HTMLFormElement) {
  try {
    const result = await fetchApiGraphQL<MeQueryResult>(meQuery, {});
    const meUser = result?.data?.me?.user;
    if (!meUser) {
      showFormMessage(form, 'failed');
      return;
    }

    const profile: UserProfile = {
      name: meUser.name,
      firstName: meUser.firstName,
      lastName: meUser.lastName,
      email: meUser.email,
      address: meUser.address,
      phoneNumber: meUser.phoneNumber,
      postalCode: meUser.postalCode,
      emailVerified: meUser.emailVerified,
    };

    loadedProfile = profile;
    fillForm(form, profile);
  } catch {
    showFormMessage(form, 'failed');
  }
}

function syncVerifyEmailButton(form: HTMLFormElement) {
  const button = form.querySelector<HTMLButtonElement>('[data-verify-email]');
  const input = form.querySelector<HTMLInputElement>('#Email');
  if (!button || !input) {
    return;
  }

  const unsavedEmail = input.value.trim() !== (loadedProfile?.email ?? '');

  button.disabled = unsavedEmail;
  form.querySelector<HTMLElement>('[data-verify-email-hint]')
    ?.classList.toggle('hidden', !unsavedEmail || button.classList.contains('hidden'));
}

function setupVerifyEmail(form: HTMLFormElement) {
  const button = form.querySelector<HTMLButtonElement>('[data-verify-email]');
  if (!button) {
    return;
  }

  form.querySelector<HTMLInputElement>('#Email')
    ?.addEventListener('input', () => syncVerifyEmailButton(form));

  button.addEventListener('click', async () => {
    showFormMessage(form, null);

    const callBackUrl = resolveCallbackUrl(form);
    if (!callBackUrl) {
      showFormMessage(form, 'verification-failed');
      return;
    }

    button.disabled = true;
    let result: SendVerificationEmailResult;
    try {
      result = await fetchApiGraphQL<SendVerificationEmailResult>(sendVerificationEmailMutation, { callBackUrl });
    } catch {
      showFormMessage(form, 'verification-failed');
      return;
    } finally {
      syncVerifyEmailButton(form);
    }

    if (!result?.data?.sendVerificationEmail) {
      showFormMessage(form, 'verification-failed');
      return;
    }

    showFormMessage(form, 'verification-sent');
  });
}

function collectChangedFields(form: HTMLFormElement): Record<string, string> {
  const changed: Record<string, string> = {};

  for (const [field, inputId] of Object.entries(fieldInputIds)) {
    const input = form.querySelector<HTMLInputElement>(`#${inputId}`);
    if (!input) {
      continue;
    }

    const value = input.value.trim();
    const original = loadedProfile?.[field as EditableField] ?? '';

    if (value !== original) {
      changed[field] = value;
    }
  }

  return changed;
}

function messageKeyForError(message: string): string {
  const text = message.toLowerCase();

  if (text.includes('already exists')) return 'email-exists';
  if (text.includes('valid email')) return 'email-invalid';
  if (text.includes('phone number is required')) return 'phone-required';

  return 'failed';
}

async function handleSubmit(e: SubmitEvent, form: HTMLFormElement) {
  e.preventDefault();
  showFormMessage(form, null);

  if (!form.querySelector<HTMLInputElement>('#FirstName')?.value.trim()) {
    showFormMessage(form, 'firstname-required');
    return;
  }

  if (!form.querySelector<HTMLInputElement>('#LastName')?.value.trim()) {
    showFormMessage(form, 'lastname-required');
    return;
  }

  if (!form.querySelector<HTMLInputElement>('#PhoneNumber')?.value.trim()) {
    showFormMessage(form, 'phone-missing');
    return;
  }

  const changed = collectChangedFields(form);

  if (Object.keys(changed).length === 0) {
    showFormMessage(form, 'success');
    return;
  }

  let result: UpdateUserProfileResult;
  try {
    result = await fetchApiGraphQL<UpdateUserProfileResult>(updateProfileMutation, changed);
  } catch (err) {
    const message = (err as GraphQLError)?.errors?.[0]?.message ?? '';
    showFormMessage(form, messageKeyForError(message));
    return;
  }

  const profile = result?.data?.updateUserProfile;
  if (!profile) {
    showFormMessage(form, 'failed');
    return;
  }

  loadedProfile = profile;
  fillForm(form, profile);
  showFormMessage(form, 'success');
}

export default function init() {
  createEnrichmentIsland('.update-user-form', async (elm) => {
    const form = elm as HTMLFormElement;

    setupProfileValidation(form);

    // Load postal codes for autocomplete
    postalCodes = await loadPostalCodes();
    setupPostalCodeAutocomplete(form);

    await whenLoggedIn();

    setupAffiliations(form).catch(() => showFormMessage(form, 'failed'));
    setupVerifyEmail(form);

    form.addEventListener('submit', (e) => handleSubmit(e as SubmitEvent, form));

    void loadProfile(form);
  });
}
